import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getBusinessBanks,
  GetBusinessBanksRequest,
  BusinessBankData,
  deleteBusinessBank,
  DeleteBusinessBankRequest,
} from '@/application/requests/businessBanks';

export interface BankState {
  banks: BusinessBankData[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;

  deleteLoading: boolean;
  deleteError: string | null;

  allBanks: BusinessBankData[];
  allBanksLoading: boolean;
  allBanksLoaded: boolean;
  allBanksError: string | null;
}

const initialState: BankState = {
  banks: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  hasNext: false,
  hasPrevious: false,
  deleteLoading: false,
  deleteError: null,

  allBanks: [],
  allBanksLoading: false,
  allBanksLoaded: false,
  allBanksError: null,
};

export const fetchBanks = createAsyncThunk(
  'bank/fetchBanks',
  async (params: GetBusinessBanksRequest, { rejectWithValue }) => {
    try {
      const response = await getBusinessBanks(params);

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to fetch banks'
        );
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch banks');
    }
  }
);

export const removeBank = createAsyncThunk(
  'bank/removeBank',
  async (id: string, { rejectWithValue }) => {
    try {
      const deleteData: DeleteBusinessBankRequest = {
        validation_response: {
          is_valid: true,
        },
      };
      const response = await deleteBusinessBank(id, deleteData);

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to delete bank'
        );
      }

      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete bank');
    }
  }
);

export const fetchAllBanks = createAsyncThunk(
  'bank/fetchAllBanks',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { bank: BankState };

    if (state.bank.allBanksLoaded) {
      return state.bank.allBanks;
    }

    try {
      const response = await getBusinessBanks({ page: 1, limit: 1000 });

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to fetch all banks'
        );
      }

      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch all banks'
      );
    }
  }
);

const bankSlice = createSlice({
  name: 'bank',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
      state.deleteError = null;
      state.allBanksError = null;
    },
    resetBanks: () => initialState,
    addToAllBanks: (state, action: PayloadAction<BusinessBankData>) => {
      if (state.allBanksLoaded) {
        state.allBanks = [action.payload, ...state.allBanks];
      }
    },
    updateInAllBanks: (state, action: PayloadAction<BusinessBankData>) => {
      if (state.allBanksLoaded) {
        const index = state.allBanks.findIndex(
          (b) => b.id === action.payload.id
        );
        if (index !== -1) {
          state.allBanks[index] = action.payload;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBanks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBanks.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.banks = action.payload.data;
          state.totalPages = action.payload.total_pages;
          state.totalItems = action.payload.total;
          state.currentPage = action.payload.page;
          state.pageSize = action.payload.limit;
          state.hasNext = action.payload.has_next;
          state.hasPrevious = action.payload.has_previous;
        }
      })
      .addCase(fetchBanks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete bank
      .addCase(removeBank.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(removeBank.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove the deleted bank from the list
        state.banks = state.banks.filter(
          (b) => b.id !== action.payload
        );
        state.totalItems = Math.max(0, state.totalItems - 1);
        // Also remove from allBanks
        if (state.allBanksLoaded) {
          state.allBanks = state.allBanks.filter(
            (b) => b.id !== action.payload
          );
        }
      })
      .addCase(removeBank.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      })
      // Fetch all banks
      .addCase(fetchAllBanks.pending, (state) => {
        if (!state.allBanksLoaded) {
          state.allBanksLoading = true;
          state.allBanksError = null;
        }
      })
      .addCase(fetchAllBanks.fulfilled, (state, action) => {
        state.allBanksLoading = false;
        state.allBanks = action.payload;
        state.allBanksLoaded = true;
      })
      .addCase(fetchAllBanks.rejected, (state, action) => {
        state.allBanksLoading = false;
        state.allBanksError = action.payload as string;
      });
  },
});

export const {
  setCurrentPage,
  setPageSize,
  clearError,
  resetBanks,
  addToAllBanks,
  updateInAllBanks,
} = bankSlice.actions;

export default bankSlice.reducer;
